import { useEffect, useState } from 'react';
import { fetchBlobUrl } from '../api';

/**
 * Load an authenticated binary endpoint into an object URL usable as an
 * <img>/<iframe>/<video> src.
 *
 * The SOP file routes require a Bearer header, which those elements cannot
 * send, so the bytes are fetched here instead. The URL is revoked on unmount
 * and whenever `url` changes — without that, browsing the library would pin
 * every thumbnail's blob in memory for the life of the page.
 *
 * Returns { url, loading, error }.
 */
const useAuthedBlob = (url, { enabled = true } = {}) => {
  const [state, setState] = useState({ url: null, loading: !!url && enabled, error: null });

  useEffect(() => {
    if (!url || !enabled) {
      setState({ url: null, loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;

    setState({ url: null, loading: true, error: null });

    fetchBlobUrl(url)
      .then((blobUrl) => {
        objectUrl = blobUrl;
        // The request can land after the component moved on; revoke instead of
        // setting state on an unmounted component.
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        setState({ url: blobUrl, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ url: null, loading: false, error: err.message || 'Failed to load' });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, enabled]);

  return state;
};

export default useAuthedBlob;
